import cloneDeep from "lodash/cloneDeep";
import { MaxItemsPerBatch } from "./constants";
import { isAdminFirestoreAdapter } from "./firestore";
/**
 * Abstracts over the below utilities to use the correct implementation for the connection type
 *
 * Admin connections can optionally use the bisecting algorithm, whereas client connections cannot.
 *
 * @param db connection to use
 * @param logger Logger instance to inject (can be `console` or Winston, etc.)
 * @param withCommitAtEnd an inner-function that will be immediately executed, and can run whatever logic you like to
 *                        decide what batch operations to perform. It receives a single argument: a function it can call
 *                        to put a single operation into the batch. At the end of the withCommitAtEnd inner-function,
 *                        all batches are awaited and combined into the final Promise value returned.
 */
export function withBatchingForConnection(db, logger, withCommitAtEnd) {
    return isAdminFirestoreAdapter(db)
        ? doBisectBatching(db, logger, withCommitAtEnd)
        : doBatching(db, logger, async (batch) => await batch.commit().then(), withCommitAtEnd);
}
/**
 * Whether we consider an error to be a candidate for bisecting the transaction that caused it
 */
function errorCanBeBisected(error) {
    const message = error?.message || "";
    return (error?.code === 3 && // 3 is GRPC for InvalidArgument
        (message.indexOf("Request payload size exceeds the limit") !== -1 ||
            message.indexOf("Transaction too big") !== -1));
}
async function doBisectBatching(db, logger, withCommitAtEnd) {
    const commitBatch = async (batch) => {
        try {
            await batch.commit();
        }
        catch (error) {
            if (errorCanBeBisected(error)) {
                const [first, second] = bisectBatchAfterError(db, batch, error, logger);
                return Promise.all([commitBatch(first), commitBatch(second)]).then();
            }
            logger.error("Error during commit", error);
            throw error;
        }
    };
    return doBatching(db, logger, commitBatch, withCommitAtEnd);
}
/**
 * Bisects the given failed batch of write operations into two
 *
 * If the batch can't be bisected, e.g. because it only has a single entry, we'll throw an Error with the provided
 * Error as a cause (representing the failure to bisect)
 */
function bisectBatchAfterError(db, failedBatch, error, logger) {
    const operations = cloneDeep(getOperationsFromWriteBatch(failedBatch));
    if (operations.length < 2) {
        logger.warn("Final failure during bisect: failed batch contains less than two operations, so no further bisecting is possible, and we will emit the failure");
        throw error;
    }
    logger.info("Bisecting upon intermediate failure", { size: operations.length, error });
    const splitPoint = Math.floor(operations.length / 2);
    const first = cloneDeep(failedBatch);
    const second = cloneDeep(failedBatch);
    setOperationsOnWriteBatch(first, operations.slice(0, splitPoint));
    setOperationsOnWriteBatch(second, operations.slice(splitPoint));
    return [first, second];
}
/**
 * Inner implementation of the batching functions
 */
async function doBatching(db, logger, commitBatch, withCommitAtEnd) {
    let batch = db.batch();
    let batchCount = 0;
    const batchPromises = [];
    const flushCurrentBatch = () => {
        batchPromises.push(commitBatch(batch));
        batchCount = 0;
        batch = db.batch();
    };
    withCommitAtEnd(
    /**
     * @param withBatch This function should call some mutation operation against the batch, such as .set() or .delete()
     *                  It either performs a single such mutation, or returns the number of mutations it did (or some such
     *                  similar number to apply backpressure to the batching algorithm, and cause it to use smaller
     *                  batches)
     */
    (withBatch) => {
        if (batchCount >= MaxItemsPerBatch) {
            flushCurrentBatch();
        }
        const incr = Math.max(0, withBatch(batch) ?? 1);
        batchCount += incr;
    });
    // Push the final batch
    if (batchCount > 0) {
        flushCurrentBatch();
    }
    // We use allSettled so that we can find and communicate the status of all the failed batches, not just the first to fail
    const results = await Promise.allSettled(batchPromises);
    const failures = results.flatMap((result) => result.status === "rejected" ? [result.reason] : []);
    if (failures.length === 1) {
        throw failures.pop();
    }
    else if (failures.length > 1) {
        throw new AggregateError(failures);
    }
}
function isAdminFirestoreUnsafeImplWriteBatch(batch) {
    return "_ops" in batch;
}
function getOperationsFromWriteBatch(batch) {
    if (isAdminFirestoreUnsafeImplWriteBatch(batch)) {
        return batch._ops;
    }
    throw new Error("Unknown batch implementation type");
}
function setOperationsOnWriteBatch(batch, operations) {
    if (isAdminFirestoreUnsafeImplWriteBatch(batch)) {
        batch._ops = operations;
        return;
    }
    throw new Error("Unknown batch implementation type");
}
//# sourceMappingURL=batch.js.map